import { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
// The package's normal entry, as in WordImport: mammoth.browser carries its own
// frozen copy of JSZip (pen test AD25996).
import mammoth from 'mammoth';
import Modal from './Modal';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

// Shows a stored file (a CV, a candidate's documents) in a dialog with a
// Download button, rather than the link downloading it straight away.
//
// /api/uploads always answers with an attachment and X-Frame-Options: DENY
// (pen test AD25996), so the file cannot be framed. It is fetched and drawn in
// the page instead, which needs no change to how uploads are served:
//   - a PDF is drawn page by page with pdf.js, as the QMS Library does;
//   - a .docx is converted to HTML with mammoth, as Word import does, and
//     sanitised before it touches the DOM;
//   - an image is shown from a blob: URL, which the app's CSP allows for img;
//   - anything else (an old .doc, a spreadsheet) says it cannot be shown here
//     and offers the download.
//
// A document that lives in SharePoint is fetched through
// /api/document-preview/:kind/:id (src/api/routes/documentPreview.js), which
// names the file in X-Filename: a SharePoint sharing link often carries no
// extension, so the kind is decided from that name once the file has arrived.
// The file is fetched once here and handed to the body that draws it.

const extOf = (name) => (String(name || '').match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
const IMAGE_EXTS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);

export function previewKind(filename) {
  const ext = extOf(filename);
  if (ext === 'pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (IMAGE_EXTS.has(ext)) return 'image';
  return 'none';
}

class FetchError extends Error {}

async function fetchFile(url) {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) {
    // The preview endpoint says why in its JSON body (not a SharePoint file, too large, gone).
    const said = await res.json().then(b => b?.error).catch(() => null);
    throw new FetchError(said || (res.status === 404 ? 'The file could not be found.' : `The file could not be loaded (${res.status}).`));
  }
  return res;
}

// A failed fetch says why; a file that would not convert gets the fallback.
const failure = (e, fallback) => (e instanceof FetchError ? e.message : fallback);

function PdfPages({ data, onError }) {
  const pagesRef = useRef(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // pdf.js takes ownership of the buffer it is given, so it gets a copy.
        const pdf = await pdfjsLib.getDocument({ data: data.slice(0) }).promise;
        if (cancelled) return;
        const container = pagesRef.current;
        if (!container) return;
        container.innerHTML = '';
        const width = container.offsetWidth || 760;
        setProgress({ done: 0, total: pdf.numPages });
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const vp = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(vp.width * dpr);
          canvas.height = Math.floor(vp.height * dpr);
          canvas.style.cssText = 'width:100%;display:block;background:#fff;margin-bottom:6px;box-shadow:0 1px 3px rgba(0,0,0,.25);';
          container.appendChild(canvas);
          const ctx = canvas.getContext('2d');
          ctx.scale(dpr, dpr);
          await page.render({ canvasContext: ctx, viewport: vp }).promise;
          if (!cancelled) setProgress({ done: i, total: pdf.numPages });
        }
      } catch {
        if (!cancelled) onError('This PDF could not be shown here.');
      }
    })();
    return () => { cancelled = true; };
  }, [data, onError]);

  return (
    <div className="bg-gray-100 rounded p-2">
      <div ref={pagesRef} />
      {progress.done < progress.total && (
        <div className="text-center text-[10px] text-gray-500 py-1">Showing page {progress.done} of {progress.total}…</div>
      )}
      {progress.total === 0 && <Loading />}
    </div>
  );
}

function DocxBody({ data, onError }) {
  const [html, setHtml] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await mammoth.convertToHtml({ arrayBuffer: data });
        if (!cancelled) setHtml(DOMPurify.sanitize(result.value || ''));
      } catch {
        if (!cancelled) onError('This Word document could not be shown here.');
      }
    })();
    return () => { cancelled = true; };
  }, [data, onError]);
  if (html === null) return <Loading />;
  if (!html.trim()) return <div className="text-[11px] text-gray-500">The document has no text to show.</div>;
  return (
    <div className="border border-gray-200 rounded px-5 py-4">
      <div className="qms-doc" dangerouslySetInnerHTML={{ __html: html }} />
      <div className="mt-3 pt-2 border-t border-gray-200 text-[10px] text-gray-500">Shown as text: the layout, fonts and columns of the Word file may differ. Download it to see the original.</div>
    </div>
  );
}

const IMAGE_TYPES = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp' };

function ImageBody({ data, filename }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const objectUrl = URL.createObjectURL(new Blob([data], { type: IMAGE_TYPES[extOf(filename)] || 'application/octet-stream' }));
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [data, filename]);
  if (!src) return <Loading />;
  return <img src={src} alt={filename} className="max-w-full mx-auto rounded border border-gray-200" />;
}

function Loading() {
  return (
    <div className="flex items-center justify-center gap-2 h-48 text-[11px] text-gray-500">
      <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-gold rounded-full animate-spin" />
      Loading the file…
    </div>
  );
}

/** The file's own name from the preview endpoint's X-Filename header, or null. */
export function nameFromResponse(res) {
  const raw = res?.headers?.get?.('X-Filename');
  if (!raw) return null;
  try { return decodeURIComponent(raw); } catch { return raw; }
}

export default function FilePreviewModal({ url, filename, title, openUrl, onClose }) {
  const [error, setError] = useState('');
  const [file, setFile] = useState(null); // { data, name }
  const given = filename || decodeURIComponent(String(url).split('/').pop());
  const name = file?.name || given;
  const kind = previewKind(name);

  useEffect(() => {
    let cancelled = false;
    setFile(null);
    setError('');
    (async () => {
      try {
        const res = await fetchFile(url);
        const data = await res.arrayBuffer();
        if (!cancelled) setFile({ data, name: nameFromResponse(res) || given });
      } catch (e) {
        if (!cancelled) setError(failure(e, 'The file could not be loaded.'));
      }
    })();
    return () => { cancelled = true; };
  }, [url, given]);

  const noPreview = file && kind === 'none';
  return (
    <Modal title={title || name} onClose={onClose} xl
      footer={<>
        <button type="button" onClick={onClose} className="px-3 py-1.5 text-[11px] font-semibold rounded bg-white border border-gray-200 text-navy hover:bg-gray-50">Close</button>
        {openUrl && (
          <a href={openUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 text-[11px] font-semibold rounded bg-white border border-gray-200 text-navy hover:bg-gray-50">Open in SharePoint ↗</a>
        )}
        <a href={url} download={name} className="px-3 py-1.5 text-[11px] font-semibold rounded bg-navy text-white hover:opacity-90">Download</a>
      </>}>
      {error || noPreview ? (
        <div className="flex flex-col items-center justify-center gap-2 h-40 text-center">
          <p className="text-[12px] text-gray-600">{error || `A .${extOf(name) || 'file'} file cannot be shown here.`}</p>
          <p className="text-[11px] text-gray-500">{openUrl ? 'Open it in SharePoint, or download it to open it on your computer.' : 'Download it to open it on your computer.'}</p>
        </div>
      ) : !file ? <Loading />
        : kind === 'pdf' ? <PdfPages data={file.data} onError={setError} />
        : kind === 'docx' ? <DocxBody data={file.data} onError={setError} />
        : <ImageBody data={file.data} filename={name} />}
    </Modal>
  );
}
